import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Plus, X, Ruler, Shirt, RefreshCw, Move3d, Pencil, Undo2 } from "lucide-react";

const FAQS = [
  {
    q: "Do KAKAIYS leather jackets run true to size?",
    a: "Yes — KAKAIYS jackets are designed to run true to size. Find your body chest or bust measurement in the chart and that is your correct size. You do not need to size up speculatively. The only reason to consider sizing up is if you plan to wear a thick hoodie or heavy knit underneath, which requires additional chest volume that the standard fit doesn't have.",
    open: true,
  },
  {
    q: "What if my measurements fall between two sizes?",
    a: "If your chest measurement falls exactly on the boundary between two sizes, choose the larger size — provided the shoulder seam of that size will sit correctly at your shoulder tip. A slightly roomy chest in leather is easily managed with a fitted base layer underneath; a too-tight chest has no remedy. The exception is if sizing up would cause the shoulder seam to hang noticeably off your shoulder, in which case the smaller size is the better choice and the chest fit will naturally be close.",
  },
  {
    q: "Can I alter a leather jacket if the size isn't quite right?",
    a: "Leather can be altered, but with significant limitations. The chest and body can be taken in by a skilled leather tailor if the jacket is too large. The sleeve length can be shortened. The waist can be adjusted slightly. However, the shoulder seam cannot practically be moved — it would require completely disassembling and rebuilding the jacket at a cost that typically exceeds the jacket's value. This is why getting the shoulder right at purchase is so important. Always prioritise the shoulder fit and allow the chest to be managed through alteration if needed.",
  },
  {
    q: "Will the leather jacket stretch and become more comfortable over time?",
    a: "Full-grain lambskin does adapt to your body over time — it softens at flex points, the chest gains a small amount of ease as the leather molds to your movement patterns, and the overall fit becomes more personal and comfortable with regular wear. However, this adaptation is modest — typically 1–2 cm of additional ease at the chest over several months of wear. A jacket that is significantly too tight will not break in to correct fit. Buy the right size from the chart; the leather will refine the fit over time rather than create it.",
  },
  {
    q: "How do I measure if I don't have a flexible measuring tape?",
    a: "Use a piece of string or ribbon: wrap it around the fullest part of your chest, mark where it meets, then measure the string against a ruler or any rigid tape measure. This works reliably for chest measurements. For shoulder width, you can use the wall method described above — mark both shoulder tips on the wall and measure the distance between the marks. Accuracy within half an inch is sufficient for size chart purposes.",
  },
  {
    q: "I want to size up for a hoodie underneath — how do I know which size to choose?",
    a: "For a standard-weight hoodie, sizing up one size from your body measurement is sufficient. This adds approximately 4–6 cm of additional chest ease compared to your standard fit — enough for a hoodie to sit comfortably underneath without the jacket pulling across the back. For a very thick or oversized hoodie, sizing up two sizes may be needed. In both cases, pay close attention to the shoulder seam when the jacket arrives — if it hangs significantly off the shoulder tip, the size is too large for your frame even with a hoodie underneath.",
  },
  {
    q: "What is the difference between men's and women's sizing at KAKAIYS?",
    a: "Men's and women's jackets at KAKAIYS are cut to different pattern geometries, not just different measurements. Women's jackets have more waist suppression (a shaped waist), a slightly sloped shoulder construction, and a higher armhole — reflecting the typical female torso proportions. Men's jackets have a more straight-cut body, squarer shoulder construction, and a deeper armhole. These are structural differences, not just size differences. Always shop the correct gender category for the best fit. If you want to size across gender categories, write to the atelier for guidance on what to expect.",
  },
  {
    q: "What if the jacket I receive doesn't fit correctly?",
    a: "KAKAIYS offers complimentary worldwide exchanges within 30 days. If the fit isn't right when the jacket arrives, contact the atelier and arrange an exchange for the correct size. To make the exchange as smooth as possible, note specifically what aspect of the fit doesn't work — too tight in the chest, shoulder seam position, sleeve length — so the correct replacement size can be identified confidently rather than by guesswork.",
  },
];

const STEPS = [
  {
    n: 1,
    title: "Chest / Bust Measurement",
    body:
      "Wrap a soft measuring tape around the fullest part of your chest — across the shoulder blades at the back, under the armpits, and across the widest point of the chest or bust at the front. Keep the tape parallel to the floor. Arms relaxed at your sides. One finger of clearance between tape and body — snug but not tight. This is your body chest measurement in inches.",
  },
  {
    n: 2,
    title: "Shoulder Width (Critical for Leather)",
    body:
      "Measure across the back from shoulder tip to shoulder tip — from one acromion (the bony point at the very edge of each shoulder) to the other. This is your across-shoulder measurement. In leather, this is the number that matters most: the shoulder seam cannot be moved after a jacket is made. If you can only get one measurement right, make it this one.",
  },
  {
    n: 3,
    title: "Measuring Alone — the Wall Method",
    body:
      "For solo shoulder measurement: stand with your back lightly against a wall, mark your two shoulder tips on the wall with a light pencil mark, then step away and measure between the marks. Far more accurate than trying to reach around yourself with a tape. For chest measurement alone, a full-length mirror helps confirm the tape is level at the back.",
  },
  {
    n: 4,
    title: "Measure Twice, Use the Larger Reading",
    body:
      "Take each measurement twice. If the two readings differ, use the larger one — particularly for shoulder width. A jacket sized to the slightly narrower reading will feel restrictive; one sized to the slightly wider reading will fit correctly. In leather, tight is always the wrong error.",
  },
];

const TIPS = [
  {
    icon: Shirt,
    title: "Measure Over a Fitted Tee",
    body:
      "Take your chest measurement while wearing the base layer you'll typically wear under the jacket — not over a thick jumper, not bare-chested. This gives you a realistic fitting measurement.",
  },
  {
    icon: Ruler,
    title: "Shoulder Seam = Non-Negotiable",
    body:
      "In leather, the shoulder seam cannot be moved by a tailor without essentially rebuilding the jacket. If the seam falls off your shoulder tip, the jacket will never look right. Prioritise this above all other measurements.",
  },
  {
    icon: RefreshCw,
    title: "Leather Runs True to Size",
    body:
      "KAKAIYS jackets run true to size — no need to size up speculatively. Simply find your chest or bust measurement in the chart and that is your size. Only consider sizing up if you plan to layer heavily underneath.",
  },
  {
    icon: Move3d,
    title: "Test Range of Motion",
    body:
      "A correct leather jacket fit allows you to cross your arms, reach forward, and sit down without pulling or significant restriction. If you receive your jacket and any of these feel restricted, it may be too small.",
  },
  {
    icon: Pencil,
    title: "Inches, Not Centimetres",
    body:
      "KAKAIYS size charts use inches. If your tape measure shows centimetres, divide by 2.54 to convert — or use the conversion: 1 inch = 2.54 cm. Most online converters work well for this.",
  },
  {
    icon: Undo2,
    title: "Free Returns — Order with Confidence",
    body:
      "If you receive your jacket and the fit isn't right, KAKAIYS offers complimentary 30-day exchanges worldwide. Measure carefully, order confidently, and exchange if needed — with no hassle.",
  },
];

const MEN = [
  ["XS", "37 – 38"],
  ["S", "39 – 40"],
  ["M", "41 – 42"],
  ["L", "43 – 44"],
  ["XL", "45 – 46"],
  ["XXL", "47 – 48"],
  ["3XL", "49 – 50"],
];

const WOMEN = [
  ["XS", "4", "33 – 34"],
  ["S", "6", "35 – 36"],
  ["M", "8", "37 – 38"],
  ["L", "10", "39 – 40"],
  ["XL", "12", "41 – 43"],
  ["XXL", "14", "44 – 46"],
  ["3XL", "16", "47 – 49"],
];

export default function SizingGuideArticle({ hero }: { hero: string }) {
  return (
    <div>
      {/* HERO */}
      <section className="relative h-[70svh] min-h-[480px] w-full overflow-hidden bg-ink">
        <img
          src={hero}
          alt="Fit & Sizing Guide"
          className="absolute inset-0 h-full w-full object-cover opacity-80 ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/30 to-ink/90" />
        <div className="relative z-10 flex h-full items-end pb-16 md:pb-24">
          <div className="container-luxe text-bone max-w-4xl">
            <div className="eyebrow text-gold">Fit & Sizing Guide</div>
            <h1 className="mt-5 font-serif text-4xl md:text-7xl leading-[1.05] text-slate-50 bg-inherit">
              How to Choose the Right Size When Buying a Leather Jacket
            </h1>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="container-luxe py-16 md:py-20 max-w-3xl">
        <blockquote className="border-l-2 border-gold pl-6 italic font-serif text-xl md:text-2xl leading-snug text-foreground">
          Leather jackets don't forgive sizing errors the way fabric garments do. The shoulder
          can't be altered. The leather won't stretch to compensate. Getting the size right
          before you buy is the single most important thing you can do — and it takes less than
          five minutes.
        </blockquote>
        <p className="mt-8 text-base md:text-lg leading-relaxed text-muted-foreground">
          Unlike a cotton shirt or a knit jumper, a leather jacket has very specific fit
          requirements that can't easily be fixed after the fact. A shoulder seam in the wrong
          position is essentially permanent. A chest that's too tight won't loosen meaningfully
          over time. This guide walks you through exactly how to measure yourself, how to read
          KAKAIYS's size charts for both men and women, and how to make the right call when
          you're between sizes.
        </p>
      </section>

      {/* STEP 1 — MEASURE */}
      <section className="container-luxe max-w-3xl pb-12 md:pb-16">
        <div className="border-t border-border pt-12 md:pt-16">
          <h2 className="font-serif text-3xl md:text-4xl mb-4">
            Step 1 — Measure Yourself Correctly
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Before comparing anything to a size chart, you need two accurate measurements: your{" "}
            <strong className="text-foreground">chest or bust circumference</strong> and your{" "}
            <strong className="text-foreground">shoulder width</strong>. Everything else is
            secondary. Here's exactly how to take them:
          </p>
          <div className="space-y-4">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="flex gap-5 rounded-sm border border-border bg-card p-5 md:p-6"
              >
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-ink text-bone font-serif text-sm">
                  {s.n}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">{s.title}</h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STEP 2 — SIZE CHARTS */}
      <section className="container-luxe max-w-4xl py-12 md:py-16">
        <div className="border-t border-border pt-12 md:pt-16">
          <h2 className="font-serif text-3xl md:text-4xl mb-4">
            Step 2 — Find Your Size in the Charts
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-10">
            KAKAIYS jackets run true to size. Compare your body chest measurement directly to
            the chart below — no need to add ease, the fit allowance is already built into each
            size. Both charts show body measurements in inches.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {/* MEN */}
            <div className="border border-border rounded-sm overflow-hidden">
              <div className="bg-ink text-bone p-5 text-center">
                <div className="font-serif text-xl">Men's Size Chart</div>
                <div className="eyebrow text-bone/70 mt-1">Body Measurements (Chest)</div>
              </div>
              <div className="bg-gold/15 px-5 py-2 text-center text-xs uppercase tracking-[0.24em] text-gold font-medium">
                ✓ Runs True to Size
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      Size
                    </th>
                    <th className="px-5 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      Chest (in)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {MEN.map((row, i) => (
                    <tr key={row[0]} className={i % 2 ? "bg-muted/20" : ""}>
                      <td className="px-5 py-3 font-medium text-foreground">{row[0]}</td>
                      <td className="px-5 py-3 text-foreground">{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="border-t border-border p-5">
                <div className="eyebrow mb-1 text-foreground">Size Guide</div>
                <p className="text-xs text-muted-foreground">
                  Measure the broadest part of your chest, keeping tape level and snug.
                </p>
              </div>
            </div>

            {/* WOMEN */}
            <div className="border border-border rounded-sm overflow-hidden">
              <div className="bg-ink text-bone p-5 text-center">
                <div className="font-serif text-xl">Women's Size Chart</div>
                <div className="eyebrow text-bone/70 mt-1">Body Measurements (Bust)</div>
              </div>
              <div className="bg-gold/15 px-5 py-2 text-center text-xs uppercase tracking-[0.24em] text-gold font-medium">
                ✓ Runs True to Size
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      Size
                    </th>
                    <th className="px-5 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      US
                    </th>
                    <th className="px-5 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      Bust (in)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {WOMEN.map((row, i) => (
                    <tr key={row[0]} className={i % 2 ? "bg-muted/20" : ""}>
                      <td className="px-5 py-3 font-medium text-foreground">{row[0]}</td>
                      <td className="px-5 py-3 text-foreground">{row[1]}</td>
                      <td className="px-5 py-3 text-foreground">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="border-t border-border p-5">
                <div className="eyebrow mb-1 text-foreground">Size Guide</div>
                <p className="text-xs text-muted-foreground">
                  Measure the broadest part of your bust, keeping tape level and snug.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 border-l-2 border-gold bg-muted/30 p-6">
            <div className="eyebrow mb-2 text-gold">✎ How to Read These Charts</div>
            <p className="italic font-serif text-foreground leading-relaxed">
              Find your chest or bust measurement in inches and match it to a size. If your
              measurement falls exactly on a boundary (e.g., a 40-inch men's chest could be S or
              M), choose the larger size. Leather does not stretch to accommodate a too-tight
              fit — but a slightly larger size can always be worn over a lighter layer.
            </p>
          </div>
        </div>
      </section>

      {/* STEP 3 — BETWEEN SIZES */}
      <section className="container-luxe max-w-3xl py-12 md:py-16">
        <div className="border-t border-border pt-12 md:pt-16">
          <h2 className="font-serif text-3xl md:text-4xl mb-6">
            Step 3 — What to Do When You're Between Sizes
          </h2>
          <div className="space-y-5 text-muted-foreground leading-relaxed text-base md:text-lg">
            <p>
              Being between sizes is the most common sizing situation and the one that causes
              the most uncertainty. The rule for leather is different from other materials:{" "}
              <strong className="text-foreground">always go up, not down</strong>. Here's why —
              and how to make the larger size work.
            </p>
            <p>
              Leather has very limited stretch capacity compared to fabric. A jacket that is too
              tight in the chest will remain too tight regardless of how much you wear it — the
              collagen fibre structure simply doesn't expand enough to make a meaningful
              difference. A jacket that is one size up from your body measurement will have a
              small amount of additional chest volume that can be filled naturally with a thin
              layer underneath — a fitted tee or a light knit.
            </p>
            <p>
              The exception is shoulder width. If sizing up would put the shoulder seam
              noticeably beyond your shoulder tip — hanging down the upper arm rather than
              sitting at the shoulder bone — the larger size is not the answer. In that case,
              size down and accept that the chest may be very close-fitting, which for a slim
              profile can actually look intentional.
            </p>
          </div>

          <div className="mt-8 rounded-sm bg-ink text-bone p-6 md:p-8">
            <div className="eyebrow text-gold mb-5">Between Sizes? — Decision Guide</div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-gold font-medium mb-2">My chest is between two sizes</div>
                <p className="text-sm text-bone/90 leading-relaxed mb-2">
                  → Size UP. A slightly roomy chest can be worn over a light layer. A too-tight
                  chest will always feel tight.
                </p>
                <p className="text-xs text-bone/60 mb-3">
                  Shoulder seam must still sit at your shoulder tip — check first.
                </p>
                <div className="text-xs text-gold">✓ Recommended for most buyers</div>
              </div>
              <div className="md:border-l md:border-bone/20 md:pl-6">
                <div className="text-gold font-medium mb-2">
                  Sizing up moves the shoulder too far
                </div>
                <p className="text-sm text-bone/90 leading-relaxed mb-2">
                  → Size DOWN and accept the close chest fit. Shoulder seam is the unalterable
                  measurement — prioritise it above all else.
                </p>
                <div className="text-xs text-gold mt-3">
                  ⚠ Only if shoulder seam hangs off the arm
                </div>
              </div>
            </div>
          </div>

          <p className="mt-6 text-center italic font-serif text-muted-foreground">
            The shoulder seam position is the one measurement that cannot be corrected after
            purchase. Every other dimension can be accommodated — the shoulder cannot.
          </p>
        </div>
      </section>

      {/* STEP 4 — FIT TIPS */}
      <section className="container-luxe max-w-5xl py-12 md:py-16">
        <div className="border-t border-border pt-12 md:pt-16">
          <h2 className="font-serif text-3xl md:text-4xl mb-8">Step 4 — Fit Tips Before You Order</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {TIPS.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.title}
                  className="border border-border bg-card p-6 hover:border-gold/40 transition-colors"
                >
                  <Icon className="w-6 h-6 text-gold mb-4" strokeWidth={1.5} />
                  <h3 className="font-semibold text-foreground mb-2">{t.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LAYERING */}
      <section className="container-luxe max-w-3xl py-12 md:py-16">
        <div className="border-t border-border pt-12 md:pt-16">
          <h2 className="font-serif text-3xl md:text-4xl mb-6">
            Layering — How It Affects Your Size Choice
          </h2>
          <div className="space-y-5 text-muted-foreground leading-relaxed text-base md:text-lg">
            <p>
              One of the most common reasons people size up in a leather jacket is to
              accommodate a layer underneath — a hoodie, a thick knit, or a mid-layer for colder
              months. If this is your intention, going one size up from your body measurement is
              the right call. The additional chest volume in the next size will accommodate a
              standard-weight knit or light hoodie comfortably without the jacket pulling across
              the back.
            </p>
            <p>
              For very heavy layers — heavyweight hoodies, thick wool knits — going up two sizes
              may be needed. In this case, pay special attention to the shoulder seam when the
              jacket arrives: two sizes up may create a noticeable shoulder seam drop. Try the
              jacket with the intended layer first and assess the shoulder position before
              deciding whether the fit is working.
            </p>
            <p>
              For a clean, fitted silhouette without layers — the look in most KAKAIYS product
              photos — your body measurement size is correct. The jackets have sufficient ease
              built in for comfortable movement over a fitted tee or shirt without any
              additional volume.
            </p>
          </div>

          <div className="mt-8 border-l-2 border-gold bg-gold/5 p-6">
            <div className="eyebrow mb-2 text-gold">✓ Quick Size Checklist</div>
            <p className="italic font-serif text-foreground leading-relaxed">
              Before ordering: measure your chest or bust (in inches) → find your size in the
              chart → confirm the shoulder width measurement is consistent with that size →
              decide whether you want a fitted or layering silhouette → order. The entire
              process takes five minutes and eliminates the most common sizing errors.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-luxe max-w-3xl py-12 md:py-16">
        <div className="border-t border-border pt-12 md:pt-16">
          <h2 className="font-serif text-3xl md:text-4xl mb-8">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} defaultOpen={!!f.open} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-luxe max-w-3xl pb-20">
        <div className="bg-ink text-bone p-10 md:p-14 text-center">
          <h3 className="font-serif text-2xl md:text-3xl tracking-wide uppercase mb-4">
            Ready to Find Your Size?
          </h3>
          <p className="text-bone/90 max-w-md mx-auto mb-8 leading-relaxed">
            Measure your chest or bust, find your size in the chart above, and order with
            confidence. Free worldwide shipping. 30-day complimentary exchanges.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/men"
              className="inline-flex items-center justify-center rounded-full bg-bone text-ink px-7 py-3 text-xs uppercase tracking-[0.24em] font-medium hover:bg-gold hover:text-ink transition-colors"
            >
              Shop Men's Jackets
            </Link>
            <Link
              to="/women"
              className="inline-flex items-center justify-center rounded-full border border-bone text-bone px-7 py-3 text-xs uppercase tracking-[0.24em] font-medium hover:bg-bone hover:text-ink transition-colors"
            >
              Shop Women's Jackets
            </Link>
          </div>
        </div>

        <div className="mt-10 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Explore More at KAKAIYS:</span>{" "}
          <Link to="/men" className="underline hover:text-gold">Men's Leather Jackets</Link> ·{" "}
          <Link to="/women" className="underline hover:text-gold">Women's Leather Jackets</Link> ·{" "}
          <Link to="/shop" className="underline hover:text-gold">All Jackets</Link> ·{" "}
          <Link to="/our-story" className="underline hover:text-gold">Our Story</Link> ·{" "}
          <Link to="/journal" className="underline hover:text-gold">The Journal</Link>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a, defaultOpen = false }: { q: string; a: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-5 py-4 text-left transition-colors ${
          open ? "bg-ink text-bone" : "bg-card text-foreground hover:bg-muted/40"
        }`}
      >
        <span className="font-medium pr-4">{q}</span>
        {open ? (
          <X className="w-4 h-4 flex-shrink-0 opacity-70" />
        ) : (
          <Plus className="w-4 h-4 flex-shrink-0 opacity-70" />
        )}
      </button>
      {open && (
        <div className="bg-card px-5 py-5 border-t border-border">
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}
